import { useOnKeyUp } from '@deltasierra/react-hooks';
import Modal from '@material-ui/core/Modal';
import Fade from '@material-ui/core/Fade';
import ChevronLeft from '@material-ui/icons/ChevronLeft';
import ChevronRight from '@material-ui/icons/ChevronRight';
import * as React from 'react';
import { DSGrid } from '../mui/Grid';
import { DSTypography } from '../mui/Typography';
import { DSIconButton } from '../Generic/Buttons/DSIconButton';
import { LightboxMedia } from './LightboxMedia';
import { LightboxModalDefaultButtons } from './LightboxModalDefaultButtons';
import {
    LightboxContextProvider,
    LightboxActionType,
    useLightboxDispatch,
    useLightboxState,
    LightboxMediaType,
} from './lightbox-context';

export interface LightboxModalProps {
    children?: React.ReactNode;
    elementRef?: React.MutableRefObject<HTMLDivElement | null>;
    isOpen: boolean;
    onRequestClose?: () => void;
}

export function LightboxModal({
    children,
    elementRef,
    isOpen,
    onRequestClose,
}: LightboxModalProps): JSX.Element {
    const { elements, index } = useLightboxState();
    const dispatch = useLightboxDispatch();
    const goToNextImage = React.useCallback(
        () => dispatch({ payload: 1, type: LightboxActionType.ShiftIndex }),
        [dispatch],
    );
    const goToPreviousImage = React.useCallback(
        () => dispatch({ payload: -1, type: LightboxActionType.ShiftIndex }),
        [dispatch],
    );
    const handleRequestClose = React.useCallback(() => {
        onRequestClose?.();
    }, [onRequestClose]);
    /*
     * Should we create a `useOnKeyDown` hook?
     * MSN docs prefers down, and we can return a ref that attaches to an element
     */
    useOnKeyUp(['ArrowRight', 'ArrowDown'], goToNextImage);
    useOnKeyUp(['ArrowLeft', 'ArrowUp'], goToPreviousImage);
    useOnKeyUp('Escape', handleRequestClose);

    return (
        <Modal
            open={isOpen}
            style={{ alignItems: 'center', display: 'flex', justifyContent: 'center' }}
            onClose={handleRequestClose}
        >
            <Fade in={isOpen}>
                <DSGrid
                    alignItems="center"
                    aria-roledescription="carousel"
                    container
                    direction="row"
                    justifyContent="space-between"
                    role="dialog"
                    style={{ height: '100%', position: 'relative' }}
                >
                    <DSGrid container justifyContent="flex-end" style={{ position: 'absolute', top: 0 }}>
                        {children}
                    </DSGrid>
                    <DSGrid item>
                        <DSIconButton
                            aria-label="previous"
                            style={{ color: 'white', margin: '12px' }}
                            onClick={goToPreviousImage}
                        >
                            <ChevronLeft />
                        </DSIconButton>
                    </DSGrid>
                    <DSGrid item style={{ flexBasis: '80%', textAlign: 'center' }}>
                        {elements[index] && (
                            <>
                                <LightboxMedia
                                    alt={elements[index].title}
                                    elementRef={elementRef}
                                    src={elements[index].url}
                                    type={elements[index].type}
                                />
                                <DSTypography align="center" style={{ color: 'white' }}>
                                    {elements[index].title}
                                </DSTypography>
                            </>
                        )}
                    </DSGrid>
                    <DSGrid item>
                        <DSIconButton
                            aria-label="next"
                            style={{ color: 'white', margin: '12px' }}
                            onClick={goToNextImage}
                        >
                            <ChevronRight />
                        </DSIconButton>
                    </DSGrid>
                </DSGrid>
            </Fade>
        </Modal>
    );
}
LightboxModal.displayName = 'LightboxModal';
LightboxModal.Provider = LightboxContextProvider;
LightboxModal.MediaType = LightboxMediaType;
LightboxModal.DefaultButtons = LightboxModalDefaultButtons;
